import { useSyncExternalStore } from 'react'

// ONE writer tab for collections.
//
// Every tab asks for the same Web Lock and holds it for its whole life. The
// tab that holds it is the LEADER and is the only one that writes collection
// state: imports, refresh, remove, claim, saved_collections upserts and the
// legacy migration. Other tabs are read-only for collections and pick up the
// leader's writes through the `storage` event. When the leader closes, the
// browser hands the lock to the next waiting tab, which becomes the leader.
//
// This replaces all cross-tab concurrency machinery: two tabs never write the
// same cache, so there is nothing to merge or compare-and-swap.
//
// No navigator.locks (an older browser): the tab is the leader, which is the
// single-tab behavior the app always had. Locks that EXIST but refuse the
// request fail closed: this tab is read-only, because another tab may well
// hold the lock and acting alone would make two writers.

export const COLLECTION_WRITER_LOCK = 'cb-collection-writer'

let leader = false
// navigator.locks exists but the request was rejected: read-only, for good.
let unavailable = false
let started = false
const listeners = new Set<() => void>()

function notify() {
  for (const l of listeners) l()
}

type LockManagerLike = { request: (name: string, cb: () => Promise<unknown>) => Promise<unknown> }

function lockManager(): LockManagerLike | null {
  if (typeof navigator === 'undefined') return null
  const locks = (navigator as Navigator & { locks?: LockManagerLike }).locks
  return locks && typeof locks.request === 'function' ? locks : null
}

/** Ask for the writer lock (idempotent). Called on first read. */
export function startCollectionLeaderElection(): void {
  if (started) return
  started = true
  const locks = lockManager()
  if (!locks) {
    leader = true
    notify()
    return
  }
  locks.request(COLLECTION_WRITER_LOCK, () => {
    leader = true
    notify()
    // Held until the tab goes away: this promise never settles.
    return new Promise<never>(() => {})
  }).catch(() => {
    // The lock request itself failed (a sandboxed frame, a revoked
    // permission). Fail closed: never a second writer.
    leader = false
    unavailable = true
    notify()
  })
}

/** True when this tab is the collection writer. */
export function isCollectionLeader(): boolean {
  startCollectionLeaderElection()
  return leader
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  startCollectionLeaderElection()
  return () => { listeners.delete(cb) }
}

const getLeader = () => leader
const serverLeader = () => false

/** React view of isCollectionLeader. */
export function useIsCollectionLeader(): boolean {
  return useSyncExternalStore(subscribe, getLeader, serverLeader)
}

/** True when Web Locks exist but refused this tab: collection writes are unavailable here. */
export function useCollectionLockUnavailable(): boolean {
  return useSyncExternalStore(subscribe, getUnavailable, serverLeader)
}
const getUnavailable = () => unavailable

/** Non-hook read of the same (a refused import says why). */
export function collectionLockUnavailable(): boolean {
  return unavailable
}

/** Test seam: forget the election so the next read starts a new one. */
export function __resetCollectionLeader(): void {
  leader = false
  unavailable = false
  started = false
  notify()
}
